import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { ApiService, PromoCampaignListItem } from '../../../shared/services/api.service';
import {
  CampaignDialogComponent,
  CampaignDialogData,
  CampaignDialogResult,
} from './campaign-dialog.component';
import { CampaignUrlDialogComponent } from './campaign-url-dialog.component';

@Component({
  selector: 'app-campaigns',
  templateUrl: './campaigns.component.html',
  styleUrls: ['./campaigns.component.css'],
})
export class CampaignsComponent implements OnInit {
  displayedColumns: string[] = [
    'name',
    'status',
    'maxUsers',
    'users',
    'expiresAt',
    'createdAt',
    'actions',
  ];
  dataSource = new MatTableDataSource<PromoCampaignListItem>([]);
  loading = false;

  constructor(
    private readonly apiService: ApiService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadCampaigns();
  }

  loadCampaigns(): void {
    this.loading = true;
    this.apiService.listCampaigns().subscribe({
      next: (response) => {
        this.dataSource.data = response.campaigns || [];
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        const message = error?.error?.error?.message || 'Failed to load campaigns';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(CampaignDialogComponent, {
      width: '480px',
      data: { mode: 'create' } satisfies CampaignDialogData,
    });
    dialogRef.afterClosed().subscribe((result: CampaignDialogResult | undefined) => {
      if (!result) return;
      this.apiService
        .createCampaign({
          name: result.name,
          maxUsers: result.maxUsers,
          expiresAt: result.expiresAt,
        })
        .subscribe({
          next: (response) => {
            this.loadCampaigns();
            const campaignUrl = String(response?.campaign?.campaignUrl || '').trim();
            if (!campaignUrl) {
              this.snackBar.open('Campaign created', 'Close', { duration: 3000 });
              return;
            }
            this.dialog.open(CampaignUrlDialogComponent, {
              width: '520px',
              disableClose: true,
              data: {
                campaignUrl,
                campaignName: response.campaign.name,
              },
            });
          },
          error: (error) => {
            const message = error?.error?.error?.message || 'Failed to create campaign';
            this.snackBar.open(message, 'Close', { duration: 5000 });
          },
        });
    });
  }

  openEditDialog(campaign: PromoCampaignListItem): void {
    const dialogRef = this.dialog.open(CampaignDialogComponent, {
      width: '480px',
      data: {
        mode: 'edit',
        campaign,
      } satisfies CampaignDialogData,
    });
    dialogRef.afterClosed().subscribe((result: CampaignDialogResult | undefined) => {
      if (!result) return;
      this.apiService
        .updateCampaign(campaign.promoCampaignId, {
          name: result.name,
          maxUsers: result.maxUsers,
          expiresAt: result.expiresAt,
        })
        .subscribe({
          next: () => {
            this.snackBar.open('Campaign updated', 'Close', { duration: 3000 });
            this.loadCampaigns();
          },
          error: (error) => {
            const message = error?.error?.error?.message || 'Failed to update campaign';
            this.snackBar.open(message, 'Close', { duration: 5000 });
          },
        });
    });
  }

  async copyCampaignUrl(campaign: PromoCampaignListItem): Promise<void> {
    const url = String(campaign.campaignUrl || '').trim();
    if (!url) {
      this.snackBar.open('Campaign URL is unavailable', 'Close', { duration: 4000 });
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      this.snackBar.open('Campaign URL copied', 'Close', { duration: 2500 });
    } catch (_error) {
      this.snackBar.open(url, 'Close', { duration: 8000 });
    }
  }

  togglePause(campaign: PromoCampaignListItem): void {
    const nextStatus = campaign.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    this.apiService.updateCampaign(campaign.promoCampaignId, { status: nextStatus }).subscribe({
      next: () => {
        this.snackBar.open(
          nextStatus === 'PAUSED' ? 'Campaign paused' : 'Campaign resumed',
          'Close',
          { duration: 3000 }
        );
        this.loadCampaigns();
      },
      error: (error) => {
        const message = error?.error?.error?.message || 'Failed to update campaign status';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  deleteCampaign(campaign: PromoCampaignListItem): void {
    const redeemed = Number(campaign.stats?.redeemed || 0);
    const confirmMessage =
      redeemed > 0
        ? `Delete campaign "${campaign.name}"?\n\n${redeemed} signed-up user(s) will remain, but campaign attribution will be cleared and the signup link will stop working.`
        : `Delete campaign "${campaign.name}"?\n\nThe signup link will stop working. This cannot be undone.`;
    if (!confirm(confirmMessage)) return;

    this.apiService.deleteCampaign(campaign.promoCampaignId).subscribe({
      next: () => {
        this.snackBar.open('Campaign deleted', 'Close', { duration: 3000 });
        this.loadCampaigns();
      },
      error: (error) => {
        const message = error?.error?.error?.message || 'Failed to delete campaign';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString();
  }
}
